import { useCallback, useEffect, useRef, useState } from "react";
import { AlertCircle } from "lucide-react";
import {
  getSandboxAuditFilterOptions,
  listSandboxAuditThreads,
  showSandboxAuditThread,
  type SandboxAuditFilterOptions,
  type SandboxAuditThreadDetail,
  type SandboxAuditThreadSummary,
} from "@/api/sandboxAudit";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  AuditFilters,
  auditFilterParams,
  emptyAuditFilters,
  type AuditFilterValues,
} from "@/components/sandbox_audit/AuditFilters";
import { AuditThreadList } from "@/components/sandbox_audit/AuditThreadList";
import { AuditThreadSheet } from "@/components/sandbox_audit/AuditThreadSheet";
import { cn } from "@/lib/utils";

const PAGE_SIZE = 50;

function errorMessage(error: unknown, fallback: string) {
  return error instanceof Error ? error.message : fallback;
}

export default function SandboxPage({ embedded = false }: { embedded?: boolean }) {
  const [filters, setFilters] = useState<AuditFilterValues>(emptyAuditFilters);
  const [filterOptions, setFilterOptions] = useState<SandboxAuditFilterOptions | null>(null);
  const [page, setPage] = useState(1);
  const [threads, setThreads] = useState<SandboxAuditThreadSummary[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);

  const [openThread, setOpenThread] = useState<SandboxAuditThreadSummary | null>(null);
  const [threadDetail, setThreadDetail] = useState<SandboxAuditThreadDetail | null>(null);
  const [isLoadingThread, setIsLoadingThread] = useState(false);
  const [threadError, setThreadError] = useState<string | null>(null);

  const listRequest = useRef(0);
  const threadRequest = useRef(0);

  useEffect(() => {
    getSandboxAuditFilterOptions()
      .then(setFilterOptions)
      .catch(() => setFilterOptions({ people: [], teams: [] }));
  }, []);

  useEffect(() => {
    const requestId = ++listRequest.current;
    setIsLoading(true);
    setListError(null);

    const params = auditFilterParams(filters);
    params.set("page", String(page));
    params.set("page_size", String(PAGE_SIZE));

    listSandboxAuditThreads(params)
      .then((data) => {
        if (requestId !== listRequest.current) return;
        setThreads(data.conversations);
        setTotalCount(data.total_count);
      })
      .catch((error) => {
        if (requestId !== listRequest.current) return;
        setListError(errorMessage(error, "Could not load threads."));
      })
      .finally(() => {
        if (requestId === listRequest.current) setIsLoading(false);
      });
  }, [filters, page]);

  const changeFilters = useCallback((next: AuditFilterValues) => {
    setPage(1);
    setFilters(next);
  }, []);

  const openThreadFlyout = useCallback(async (thread: SandboxAuditThreadSummary) => {
    const requestId = ++threadRequest.current;
    setOpenThread(thread);
    setThreadDetail(null);
    setThreadError(null);
    setIsLoadingThread(true);
    try {
      const detail = await showSandboxAuditThread(thread.id);
      if (requestId === threadRequest.current) setThreadDetail(detail);
    } catch (error) {
      if (requestId === threadRequest.current) setThreadError(errorMessage(error, "Could not load this thread."));
    } finally {
      if (requestId === threadRequest.current) setIsLoadingThread(false);
    }
  }, []);

  const closeThread = useCallback(() => {
    threadRequest.current += 1;
    setOpenThread(null);
    setThreadDetail(null);
    setThreadError(null);
    setIsLoadingThread(false);
  }, []);

  const pageCount = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  return (
    <div className={cn(embedded ? "h-full" : "h-screen", "flex min-h-0 flex-col bg-background")}>
      <header className="space-y-4 border-b border-border bg-card px-6 py-4">
        <div>
          <h1 className="text-2xl font-semibold">Conversation Audit</h1>
          <p className="text-sm text-muted-foreground">Sandbox threads you can review. Open one to read the full transcript.</p>
        </div>
        <AuditFilters values={filters} options={filterOptions} onChange={changeFilters} />
      </header>

      {listError && (
        <div className="px-6 pt-3">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{listError}</AlertDescription>
          </Alert>
        </div>
      )}

      <section className="min-h-0 flex-1 overflow-y-auto" aria-busy={isLoading}>
        <AuditThreadList
          threads={threads}
          isLoading={isLoading}
          selectedId={openThread?.id ?? null}
          onOpen={(thread) => void openThreadFlyout(thread)}
        />
      </section>

      <footer className="flex items-center justify-between border-t border-border bg-card px-6 py-2 text-xs text-muted-foreground">
        <span>
          {totalCount === 1 ? "1 thread" : `${new Intl.NumberFormat().format(totalCount)} threads`} · Page {page} of{" "}
          {pageCount}
        </span>
        <div className="flex gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={page <= 1 || isLoading}
            onClick={() => setPage((current) => Math.max(1, current - 1))}
          >
            Previous
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={page >= pageCount || isLoading}
            onClick={() => setPage((current) => current + 1)}
          >
            Next
          </Button>
        </div>
      </footer>

      <AuditThreadSheet
        thread={openThread}
        detail={threadDetail}
        isLoading={isLoadingThread}
        error={threadError}
        onClose={closeThread}
      />
    </div>
  );
}
